<template>
  <div class="process-file-panel" :class="[`mode-${mode}`, { maximized: maximized }]">
    <div class="file-panel-header">
      <div class="file-panel-title-group">
        <span class="file-panel-title">过程文件</span>
        <span v-if="fileList.length > 0" class="file-count-badge">{{ fileList.length }}</span>
      </div>
      <div class="file-panel-actions">
        <div v-if="mode === 'push'" class="panel-icon-btn" @click="$emit('update:maximized', !maximized)">
          <LineIcon :name="maximized ? 'shrink' : 'expand'" />
        </div>
        <div class="panel-icon-btn" @click="$emit('close')">
          <LineIcon name="chevronRight" />
        </div>
      </div>
    </div>
    <div class="file-panel-body">
      <div class="file-list" :class="{ 'has-detail': selectedFile }">
        <div v-if="loading" class="file-list-loading">
          <i class="el-icon-loading"></i>
          <span>加载中...</span>
        </div>
        <div v-else-if="fileList.length === 0" class="file-list-empty">
          <i class="el-icon-document-delete"></i>
          <span>暂无过程文件</span>
        </div>
        <div
          v-else
          class="file-item"
          :class="{ active: selectedFile && selectedFile.id === item.id }"
          v-for="item in fileList"
          :key="item.id"
          @click="handleSelectFile(item)"
        >
          <div class="file-icon">
            <i :class="getFileIcon(item.fileType)"></i>
          </div>
          <div class="file-info">
            <div class="file-name" :title="item.fileName">{{ item.fileName }}</div>
            <div class="file-meta">
              <span class="file-size">{{ formatFileSize(item.fileSize) }}</span>
              <span class="file-time">{{ formatTime(item.createTime) }}</span>
            </div>
            <div class="file-tool" v-if="item.toolName">{{ item.toolName }}</div>
          </div>
          <div class="file-download" @click.stop="handleDownload(item)">
            <i class="el-icon-download"></i>
          </div>
        </div>
      </div>

      <transition name="detail-slide">
        <div v-if="selectedFile" class="file-detail">
          <div class="detail-header">
            <div class="detail-title">
              <i :class="getFileIcon(selectedFile.fileType)"></i>
              <span>{{ selectedFile.fileName }}</span>
            </div>
            <div class="detail-info">
              <span>类型：{{ selectedFile.fileType }}</span>
              <span>大小：{{ formatFileSize(selectedFile.fileSize) }}</span>
              <span>创建：{{ formatTime(selectedFile.createTime) }}</span>
            </div>
          </div>
          <div class="detail-content" v-loading="previewLoading" element-loading-text="正在加载文件内容...">
            <!-- 图片预览 -->
            <div v-if="previewType === 'image'" class="preview-image">
              <img :src="imageUrl" :alt="selectedFile.fileName" />
            </div>
            <!-- PDF 预览 -->
            <div v-else-if="previewType === 'pdf'" class="preview-pdf">
              <canvas v-for="(page, idx) in pdfPages" :key="idx" :ref="'pdfPage_' + idx" class="pdf-page"></canvas>
              <div v-if="pdfPages.length === 0 && !previewLoading" class="preview-empty">PDF 加载失败</div>
            </div>
            <!-- Word 文档预览 -->
            <div v-else-if="previewType === 'word'" class="preview-word" v-html="renderedContent"></div>
            <!-- Excel 表格预览 -->
            <div v-else-if="previewType === 'excel'" class="preview-excel" v-html="renderedContent"></div>
            <!-- Markdown 预览 -->
            <div
              v-else-if="previewType === 'markdown'"
              class="preview-markdown"
              :class="{ streaming: isStreaming }"
              v-html="renderedContent"
            ></div>
            <!-- HTML 页面回显预览 -->
            <iframe
              v-else-if="previewType === 'html'"
              class="preview-html"
              :srcdoc="previewContent"
              sandbox="allow-scripts"
            ></iframe>
            <!-- 代码高亮预览 -->
            <pre
              v-else-if="previewType === 'code'"
              class="preview-code"
              :class="{ streaming: isStreaming }"
            ><code :class="codeLanguage" v-html="renderedContent"></code></pre>
            <!-- 纯文本预览 -->
            <pre
              v-else-if="previewType === 'text'"
              class="preview-text"
              :class="{ streaming: isStreaming }"
              v-html="renderedContent"
            ></pre>
            <!-- 不支持预览 -->
            <div v-else-if="previewType === 'unsupported'" class="preview-unsupported">
              <i class="el-icon-warning"></i>
              <p>{{ unsupportedTip }}</p>
              <div class="btn-primary preview-download" @click="handleDownload(selectedFile)">
                <i class="el-icon-download"></i>
                <span>下载文件</span>
              </div>
            </div>
            <!-- 加载中 -->
            <div v-else class="preview-loading">
              <i class="el-icon-loading"></i>
              <span>准备预览...</span>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { getFilesApi, downloadFileApi } from '@/api/agentAuth'
import { renderMarkdownText } from '@/utils/markdownRenderer'
import hljs from 'highlight.js'
import Cookies from 'js-cookie'
import LineIcon from './LineIcon.vue'

const EXT_ICON_MAP = {
  pdf: 'el-icon-document',
  doc: 'el-icon-document',
  docx: 'el-icon-document',
  xls: 'el-icon-s-grid',
  xlsx: 'el-icon-s-grid',
  ppt: 'el-icon-picture',
  pptx: 'el-icon-picture',
  txt: 'el-icon-notebook-2',
  md: 'el-icon-notebook-2',
  json: 'el-icon-document-copy',
  png: 'el-icon-picture-outline',
  jpg: 'el-icon-picture-outline',
  jpeg: 'el-icon-picture-outline',
  gif: 'el-icon-picture-outline',
  bmp: 'el-icon-picture-outline',
  svg: 'el-icon-picture-outline',
  csv: 'el-icon-s-grid'
}

const CODE_EXT_MAP = {
  js: 'javascript',
  ts: 'typescript',
  py: 'python',
  java: 'java',
  c: 'c',
  cpp: 'cpp',
  cs: 'csharp',
  go: 'go',
  rs: 'rust',
  rb: 'ruby',
  php: 'php',
  sql: 'sql',
  html: 'html',
  css: 'css',
  scss: 'scss',
  less: 'less',
  json: 'json',
  xml: 'xml',
  yaml: 'yaml',
  yml: 'yaml',
  sh: 'bash',
  bash: 'bash',
  vue: 'vue',
  jsx: 'javascript',
  tsx: 'typescript'
}

const MARKDOWN_EXTS = ['md', 'markdown']
const TEXT_EXTS = ['txt', 'log', 'csv', 'ini', 'conf', 'cfg', 'env', 'gitignore', 'yml', 'yaml']
const IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'webp']
const WORD_EXTS = ['docx']
const EXCEL_EXTS = ['xlsx', 'xls']
const PDF_EXTS = ['pdf']
const UNSUPPORTED_EXTS = ['doc', 'ppt', 'pptx', 'zip', 'rar', '7z']

export default {
  name: 'ProcessFilePanel',
  components: { LineIcon },
  props: {
    conversationId: {
      type: String,
      default: ''
    },
    mode: {
      type: String,
      default: 'overlay'
    },
    maximized: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    resolvedConversationId() {
      const key = this.conversationId
      if (!key) return ''
      const colonIdx = key.indexOf(':', key.indexOf(':') + 1) + 1
      return colonIdx > 0 ? key.slice(colonIdx) : key
    }
  },
  data() {
    return {
      fileList: [],
      loading: false,
      selectedFile: null,
      previewLoading: false,
      previewContent: '',
      renderedContent: '',
      previewType: '',
      codeLanguage: '',
      isStreaming: false,
      unsupportedTip: '',
      imageUrl: '',
      pdfPages: []
    }
  },
  watch: {
    resolvedConversationId: {
      handler(val) {
        if (val) this.fetchFileList()
      },
      immediate: true
    }
  },
  methods: {
    async fetchFileList() {
      if (!this.resolvedConversationId) return
      this.loading = true
      try {
        const res = await getFilesApi({ conversationId: this.resolvedConversationId })
        if (res && res.data) {
          this.fileList = Array.isArray(res.data) ? res.data : []
        } else {
          this.fileList = []
        }
      } catch (e) {
        console.error('[ProcessFilePanel] 获取过程文件失败:', e)
        this.fileList = []
      } finally {
        this.loading = false
      }
    },
    handleSelectFile(item) {
      this.selectedFile = item
      const ext = (item.fileType || '').toLowerCase().replace('.', '')
      this.previewType = this.determinePreviewType(ext)
      this.renderedContent = ''
      this.previewContent = ''
      this.codeLanguage = ''
      this.imageUrl = ''
      this.pdfPages = []

      if (this.previewType === 'unsupported') {
        this.unsupportedTip = `此文件为 .${ext} 格式，暂不支持在线预览，请点击下载按钮查看。`
        return
      }

      this.fetchFilePreview(item)
    },
    determinePreviewType(ext) {
      if (IMAGE_EXTS.includes(ext)) return 'image'
      if (PDF_EXTS.includes(ext)) return 'pdf'
      if (WORD_EXTS.includes(ext)) return 'word'
      if (EXCEL_EXTS.includes(ext)) return 'excel'
      if (MARKDOWN_EXTS.includes(ext)) return 'markdown'
      if (ext === 'html' || ext === 'htm') return 'html'
      if (CODE_EXT_MAP[ext]) return 'code'
      if (TEXT_EXTS.includes(ext)) return 'text'
      if (UNSUPPORTED_EXTS.includes(ext)) return 'unsupported'
      return 'text'
    },
    async fetchFilePreview(item) {
      if (!this.resolvedConversationId || !item.id) return
      this.previewLoading = true
      this.isStreaming = false

      const ext = (item.fileType || '').toLowerCase().replace('.', '')
      const isBinary = this.previewType === 'image' ||
        this.previewType === 'pdf' ||
        this.previewType === 'word' ||
        this.previewType === 'excel'

      try {
        const url = `/agent-api/claw/api/v1/conversations/${this.resolvedConversationId}/files/${item.id}/download`
        const workspaceId = this.getWorkspaceId()
        const tokenKey = process.env.VUE_APP_WS_TOKEN_KEY || 'tianzhi_test_agent_token'
        const token = Cookies.get(tokenKey) || ''

        const headers = {
          'X-Workspace-Id': workspaceId
        }
        if (token) {
          headers['Authorization'] = 'Bearer ' + token
        }

        const response = await fetch(url, { method: 'GET', headers })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)

        if (isBinary) {
          const blob = await response.blob()
          await this.handleBinaryPreview(blob, ext, item)
        } else {
          if (!response.body) {
            const text = await response.text()
            this.updatePreviewContent(text, ext)
          } else {
            this.isStreaming = true
            const reader = response.body.getReader()
            const decoder = new TextDecoder('utf-8')
            let buffer = ''
            let done = false
            while (!done) {
              const result = await reader.read()
              done = result.done
              if (done) break
              const chunk = decoder.decode(result.value, { stream: true })
              buffer += chunk
              this.updatePreviewContent(buffer, ext)
              await this.sleep(30)
            }
            const finalChunk = decoder.decode()
            if (finalChunk) {
              buffer += finalChunk
              this.updatePreviewContent(buffer, ext)
            }
          }
        }
      } catch (e) {
        console.error('[ProcessFilePanel] 预览文件失败:', e)
        this.renderedContent = `<p class="preview-error">文件预览加载失败：${e.message || '未知错误'}</p>`
      } finally {
        this.previewLoading = false
        this.isStreaming = false
      }
    },
    async handleBinaryPreview(blob, ext, item) {
      try {
        if (this.previewType === 'image') {
          this.handleImagePreview(blob)
        } else if (this.previewType === 'pdf') {
          await this.handlePdfPreview(blob)
        } else if (this.previewType === 'word') {
          await this.handleWordPreview(blob)
        } else if (this.previewType === 'excel') {
          await this.handleExcelPreview(blob, ext)
        }
      } catch (e) {
        console.error('[ProcessFilePanel] 二进制预览失败:', e)
        this.renderedContent = `<p class="preview-error">文件预览加载失败：${e.message || '未知错误'}</p>`
        this.previewType = 'unsupported'
        this.unsupportedTip = '文件解析失败，请尝试下载后查看。'
      }
    },
    handleImagePreview(blob) {
      const url = URL.createObjectURL(blob)
      this.imageUrl = url
      this._revokeImageUrl()
      this._currentImageUrl = url
    },
    _revokeImageUrl() {
      if (this._currentImageUrl) {
        setTimeout(() => URL.revokeObjectURL(this._currentImageUrl), 1000)
      }
    },
    async handlePdfPreview(blob) {
      const arrayBuffer = await blob.arrayBuffer()
      const pdfjsLib = await import(
        /* webpackChunkName: "pdfjs" */ 'pdfjs-dist'
      ).catch(() => null)
      if (!pdfjsLib) {
        this.renderedContent = '<p class="preview-error">PDF 预览组件加载失败</p>'
        return
      }
      try {
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
        this.pdfPages = new Array(pdf.numPages).fill(0)
        this.$nextTick(async () => {
          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i)
            const viewport = page.getViewport({ scale: 1.2 })
            const canvasList = this.$refs['pdfPage_' + (i - 1)]
            const canvas = Array.isArray(canvasList) ? canvasList[0] : canvasList
            if (!canvas) continue
            const context = canvas.getContext('2d')
            canvas.width = viewport.width
            canvas.height = viewport.height
            await page.render({ canvasContext: context, viewport }).promise
          }
        })
      } catch (e) {
        console.error('[ProcessFilePanel] PDF 渲染失败:', e)
        this.renderedContent = '<p class="preview-error">PDF 渲染失败</p>'
      }
    },
    async handleWordPreview(blob) {
      const arrayBuffer = await blob.arrayBuffer()
      const mammoth = await import(
        /* webpackChunkName: "mammoth" */ 'mammoth'
      ).catch(() => null)
      if (!mammoth) {
        this.renderedContent = '<p class="preview-error">Word 预览组件加载失败</p>'
        return
      }
      try {
        const result = await mammoth.convertToHtml({ arrayBuffer })
        this.renderedContent = result.value
      } catch (e) {
        console.error('[ProcessFilePanel] Word 解析失败:', e)
        this.renderedContent = '<p class="preview-error">Word 文件解析失败</p>'
      }
    },
    async handleExcelPreview(blob, ext) {
      const arrayBuffer = await blob.arrayBuffer()
      const XLSX = await import(
        /* webpackChunkName: "xlsx" */ 'xlsx'
      ).catch(() => null)
      if (!XLSX) {
        this.renderedContent = '<p class="preview-error">Excel 预览组件加载失败</p>'
        return
      }
      try {
        const wb = XLSX.read(arrayBuffer, { type: 'array' })
        let html = ''
        wb.SheetNames.forEach((name) => {
          const sheet = wb.Sheets[name]
          const tableHtml = XLSX.utils.sheet_to_html(sheet, { editable: false })
          html += `<div class="excel-sheet-name">${name}</div>` + tableHtml
        })
        this.renderedContent = html
      } catch (e) {
        console.error('[ProcessFilePanel] Excel 解析失败:', e)
        this.renderedContent = '<p class="preview-error">Excel 文件解析失败</p>'
      }
    },
    updatePreviewContent(text, ext) {
      this.previewContent = text

      switch (this.previewType) {
        case 'markdown':
          this.renderedContent = renderMarkdownText(text)
          break
        case 'html':
          // 直接把原始 html 文本交给 iframe 的 srcdoc 回显（previewContent 已赋值），无需高亮/转义
          break
        case 'code':
          this.codeLanguage = CODE_EXT_MAP[ext] || 'plaintext'
          try {
            this.renderedContent = hljs.highlight(text, {
              language: this.codeLanguage,
              ignoreIllegals: true
            }).value
          } catch {
            this.renderedContent = hljs.highlightAuto(text).value
          }
          break
        case 'text':
          this.renderedContent = this.escapeHtml(text)
          break
        default:
          this.renderedContent = this.escapeHtml(text)
      }

      this.$nextTick(() => {
        const container = this.$el.querySelector('.detail-content')
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },
    escapeHtml(str) {
      const div = document.createElement('div')
      div.textContent = str
      return div.innerHTML
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    getWorkspaceId() {
      try {
        return localStorage.getItem('claw_workspace_id') || ''
      } catch {
        return ''
      }
    },
    async handleDownload(item) {
      if (!this.resolvedConversationId || !item.id) return
      try {
        const res = await downloadFileApi({
          conversationId: this.resolvedConversationId,
          fileId: item.id
        })
        if (res && res.data) {
          let blob = res.data
          if (!(blob instanceof Blob)) {
            blob = new Blob([blob])
          }
          const contentType = res.headers && res.headers['content-type']
          if (contentType && blob.type !== contentType) {
            blob = blob.slice(0, blob.size, contentType)
          }
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = item.fileName || 'download'
          a.style.display = 'none'
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          setTimeout(() => URL.revokeObjectURL(url), 5000)
        }
      } catch (e) {
        console.error('[ProcessFilePanel] 下载文件失败:', e)
        this.$message && this.$message.error('下载失败，请重试')
      }
    },
    getFileIcon(ext) {
      if (!ext) return 'el-icon-document'
      const key = String(ext).toLowerCase().replace('.', '')
      return EXT_ICON_MAP[key] || 'el-icon-document'
    },
    formatFileSize(size) {
      if (!size) return '未知大小'
      const num = Number(size)
      if (isNaN(num)) return String(size)
      if (num < 1024) return num + ' B'
      if (num < 1024 * 1024) return (num / 1024).toFixed(1) + ' KB'
      if (num < 1024 * 1024 * 1024) return (num / (1024 * 1024)).toFixed(1) + ' MB'
      return (num / (1024 * 1024 * 1024)).toFixed(2) + ' GB'
    },
    formatTime(time) {
      if (!time) return ''
      try {
        const d = new Date(time)
        const pad = (n) => String(n).padStart(2, '0')
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
      } catch {
        return String(time)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.process-file-panel {
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &.mode-overlay {
    position: absolute;
    top: 54px;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 20;
  }

  &.mode-push {
    position: relative;
    width: 600px;
    flex-shrink: 0;
    height: 100%;
    z-index: auto;
    border-left: 1px solid #f0f0f0;

    &.maximized {
      width: 100%;
      border-left: none;
    }
  }
}

.file-panel-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.file-panel-title-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.file-panel-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2329;
}

.file-panel-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.file-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  background: #4a6cf7;
  border-radius: 10px;
  line-height: 1;
}

/* 面板头部图标按钮：与会话页右上角图标按钮保持一致（24×24、圆角4px、无底色、hover 主题蓝底白字） */
.panel-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border-radius: 4px;
  background: transparent;
  color: #1f2329;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #4a5ff3;
    color: #fff;
    box-shadow: none;
  }
}

.file-panel-body {
  flex: 1;
  display: flex;
  min-height: 0;
  overflow: hidden;
}

.file-list {
  width: 100%;
  flex-shrink: 0;
  overflow-y: auto;
  padding: 12px;
  transition: width 0.25s ease;
  border-right: 1px solid #f0f0f0;

  &.has-detail {
    width: 290px;
  }
}

.file-list-loading,
.file-list-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  gap: 8px;
  color: #8f959e;
  font-size: 13px;

  i {
    font-size: 28px;
  }
}

.file-list-empty i {
  font-size: 36px;
  color: #d9d9d9;
}

.file-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 8px;
  transition: all 0.2s;
  border: 1px solid transparent;

  &:hover {
    background: #f7f8fc;
    border-color: #e8ecf0;
  }

  &.active {
    background: #f0f4ff;
    border-color: #4a5ff3;
  }
}

.file-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  background: #f0f4ff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4a5ff3;
  font-size: 18px;
}

.file-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.file-name {
  font-size: 13px;
  font-weight: 500;
  color: #1f2329;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: #8f959e;
}

.file-tool {
  font-size: 11px;
  color: #4a5ff3;
  background: #f0f4ff;
  padding: 1px 6px;
  border-radius: 3px;
  display: inline-block;
  width: fit-content;
}

.file-download {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8f959e;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #4a5ff3;
    color: #fff;
  }
}

.file-detail {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.detail-header {
  flex-shrink: 0;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.detail-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #1f2329;
  margin-bottom: 8px;
  i {
    color: #4a5ff3;
    font-size: 20px;
  }
}

.detail-info {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 12px;
  color: #8f959e;
}

.detail-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.preview-image {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 16px;
  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
  }
}

.preview-pdf {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px;
  .pdf-page {
    max-width: 100%;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }
}

.preview-word {
  font-size: 14px;
  line-height: 1.7;
  color: #1f2329;
  word-wrap: break-word;
  img {
    max-width: 100%;
  }
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 8px 0;
    th, td {
      border: 1px solid #e8ecf0;
      padding: 6px 10px;
    }
    th {
      background: #f7f8fc;
    }
  }
}

.preview-excel {
  font-size: 13px;
  line-height: 1.6;
  .excel-sheet-name {
    font-weight: 600;
    font-size: 14px;
    color: #1f2329;
    margin: 12px 0 8px;
    padding: 6px 12px;
    background: #f7f8fc;
    border-radius: 4px;
    display: inline-block;
  }
  table {
    border-collapse: collapse;
    width: 100%;
    margin-bottom: 12px;
    td, th {
      border: 1px solid #e8ecf0;
      padding: 6px 10px;
      text-align: left;
      white-space: nowrap;
    }
    th {
      background: #f7f8fc;
      font-weight: 600;
    }
  }
}

.preview-text {
  font-size: 13px;
  line-height: 1.8;
  color: #1f2329;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;

  &.streaming::after {
    content: ' ▌';
    display: inline;
    animation: blink 1s step-end infinite;
    color: #4a5ff3;
  }
}

.preview-markdown {
  font-size: 14px;
  line-height: 1.7;
  color: #1f2329;

  h1, h2, h3, h4, h5, h6 {
    margin-top: 20px;
    margin-bottom: 12px;
    font-weight: 600;
  }
  h1 { font-size: 20px; }
  h2 { font-size: 18px; }
  h3 { font-size: 16px; }

  p { margin: 8px 0; }
  ul, ol { padding-left: 24px; margin: 8px 0; }
  li { margin: 4px 0; }

  code {
    background: #f0f4ff;
    color: #4a5ff3;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 13px;
    font-family: 'SFMono-Regular', Consolas, monospace;
  }

  pre {
    background: #f7f8fc;
    border-radius: 8px;
    padding: 14px 16px;
    overflow-x: auto;
    margin: 12px 0;
    code {
      background: transparent;
      padding: 0;
      color: inherit;
      font-size: 13px;
      line-height: 1.6;
    }
  }

  blockquote {
    border-left: 3px solid #4a5ff3;
    padding-left: 16px;
    margin: 12px 0;
    color: #4e5969;
    background: #f7f8fc;
    border-radius: 0 6px 6px 0;
    padding: 12px 16px;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 12px 0;
    font-size: 13px;
  }

  th, td {
    border: 1px solid #e8ecf0;
    padding: 8px 12px;
    text-align: left;
  }

  th {
    background: #f7f8fc;
    font-weight: 600;
  }

  &.streaming::after {
    content: ' ▌';
    display: inline;
    animation: blink 1s step-end infinite;
    color: #4a5ff3;
  }
}

.preview-code {
  background: #1e1e1e;
  border-radius: 8px;
  padding: 16px;
  overflow-x: auto;
  margin: 0;
  font-size: 13px;
  line-height: 1.6;

  code {
    background: transparent;
    padding: 0;
    color: #d4d4d4;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  }

  &.streaming::after {
    content: ' ▌';
    display: inline;
    animation: blink 1s step-end infinite;
    color: #4a5ff3;
  }
}

.preview-html {
  width: 100%;
  height: 100%;
  min-height: 400px;
  border: none;
  background: #fff;
  border-radius: 8px;
}

.preview-unsupported {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding-top: 60px;
  color: #8f959e;

  i {
    font-size: 48px;
    color: #d9d9d9;
  }

  p {
    margin: 0;
    font-size: 14px;
    color: #4e5969;
    text-align: center;
    line-height: 1.6;
  }
}

.preview-download {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  background: #4a5ff3;
  color: #fff;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  &:hover {
    background: #3a4fe3;
  }
}

.preview-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding-top: 60px;
  color: #8f959e;
  font-size: 13px;

  i {
    font-size: 28px;
    color: #4a5ff3;
  }
}

.preview-error {
  color: #f56c6c;
  font-size: 13px;
  padding: 20px;
}

.preview-empty {
  text-align: center;
  padding: 40px;
  color: #8f959e;
}

@keyframes blink {
  50% {
    opacity: 0;
  }
}

.detail-slide-enter-active,
.detail-slide-leave-active {
  transition: all 0.25s ease;
}

.detail-slide-enter,
.detail-slide-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}
</style>
